import { supabase } from '../supabase/config';
import { formatDateTime } from '../utils/dateTime';
import { getGoogleMapsUrl } from '../utils/geo';
import { getAllEvents } from '../supabase/events';

const updateProgressBar = (eventId, startTime, endTime) => {
  try {
    const statusContainer = document.getElementById(`status_${eventId}`);
    
    if (!statusContainer) return false;

    // ✅ USE MASTER STATUS CALCULATOR (handles timezone correctly)
    const status = window.calculateEventStatus(startTime, endTime);
    
    if (status.hasEnded) {
      return false;
    }
    
    const progressColor = status.statusColor;
    const progressText = status.statusText;
    
    // Update the circle color
    const statusCircle = document.getElementById(`circle_${eventId}`);
    if (statusCircle) {
      statusCircle.style.background = progressColor;
    }
    
    // Update the pulse color
    const statusPulse = document.getElementById(`pulse_${eventId}`);
    if (statusPulse) {
      statusPulse.style.borderColor = progressColor;
    }
    
    // Update the text
    const statusText = document.getElementById(`text_${eventId}`);
    if (statusText) {
      statusText.textContent = progressText;
      statusText.style.color = progressColor;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

// Function to update event card styling based on current status
const updateEventCardStatus = (eventId, event) => {
  try {
    // ✅ USE MASTER STATUS CALCULATOR (handles timezone correctly)
    const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
    const hasEnded = status.hasEnded;
    const borderColor = status.statusColor;
    
    // Find the event card by looking for any element with this eventId
    const eventElements = document.querySelectorAll(`[id*="${eventId}"]`);
    let eventCard = null;
    
    for (let elem of eventElements) {
      const card = elem.closest('div[style*="border:"]');
      if (card) {
        eventCard = card;
        break;
      }
    }
    
    if (!eventCard) return;
    
    // Update border color
    const currentStyle = eventCard.getAttribute('style');
    const updatedStyle = currentStyle.replace(/border: \d+px solid [^;]+;/, `border: 2px solid ${borderColor};`);
    eventCard.setAttribute('style', updatedStyle);
    
    // Update pulse effect border color
    const pulseElement = eventCard.querySelector('div[style*="animation"]');
    if (pulseElement) {
      const pulseStyle = pulseElement.getAttribute('style');
      const updatedPulseStyle = pulseStyle.replace(/border: \d+px solid [^;]+;/, `border: 2px solid ${borderColor};`);
      pulseElement.setAttribute('style', updatedPulseStyle);
      
      // Remove pulse animation if finished
      if (hasEnded) {
        pulseElement.style.opacity = '0';
        pulseElement.style.animation = 'none';
      }
    }
  } catch (error) {
    console.error('Event card status update error:', error);
  }
};

export const showEventsListPanel = async (placeId, placeName, placeAddress, lat, lng) => {

    // Close any open panels first
  window.closeAllPanels();

if (window.cleanupEventIntervals) {
    window.cleanupEventIntervals();
  }

  // Use cached events if available, fall back to fresh fetch
  let events;
  if (window.allEvents && window.allEvents.length > 0) {
    events = window.allEvents;
  } else {
    const { success, events: fetched } = await getAllEvents();
    if (!success) return;
    events = fetched;
    window.allEvents = events;
  }

// Filter events for this specific place AND exclude archived events
const allEventsAtPlace = events.filter(event => {
  if (event.archived) return false;
  if (event.placeId === placeId) return true;
  // Also match by coordinates if lat/lng provided (handles different placeIds at same location)
  if (lat && lng) {
    const latDiff = Math.abs((event.latitude || 0) - lat);
    const lngDiff = Math.abs((event.longitude || 0) - lng);
    return latDiff < 0.0001 && lngDiff < 0.0001;
  }
  return false;
});

// Apply category filter (same as map markers)
const activeCategories = window.selectedCategories || [];
const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
const _cu = window.currentAppUser || window.currentUser;
const _userGender = _cu?.profile?.gender;
const _userDob = _cu?.profile?.dateOfBirth;
const now = new Date();

const eventsAtPlace = allEventsAtPlace.filter(event => {
  const isOwn = event.creatorId === currentUserId;
  if (isOwn) return true; // creator always sees their own event

  // Gender filter
  const eventGenderPref = event.genderPreference || ["man", "woman", "different"];
  const hasGenderRestriction = !(eventGenderPref.includes("man") && eventGenderPref.includes("woman") && eventGenderPref.includes("different"));
  if (hasGenderRestriction && !_userGender) return false;
  if (_userGender && !eventGenderPref.includes(_userGender)) return false;

  // Age filter
  const eventAgeMin = event.ageMin || 18;
  const eventAgeMax = event.ageMax || 99;
  const hasAgeRestriction = !(eventAgeMin === 18 && eventAgeMax === 99);
  if (hasAgeRestriction) {
    if (!_userDob) return false;
    const birthDate = new Date(_userDob);
    let userAge = now.getFullYear() - birthDate.getFullYear();
    const monthDiff = now.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) userAge--;
    if (userAge < eventAgeMin || userAge > eventAgeMax) return false;
  }

  // Category filter
  if (activeCategories.length > 0) {
    const eventCats = Array.isArray(event.category) ? event.category : [event.category || 'place'];
    if (!eventCats.some(c => activeCategories.includes(c))) return false;
  }

  return true;
});

  // 🆕 Fetch organizer data for all events at this location
  const organizersMap = {};
  const uniqueCreatorIds = [...new Set(eventsAtPlace.map(e => e.creatorId))];
  
  const otherCreatorIds = uniqueCreatorIds.filter(id => id !== window.currentAppUser?.uid);
  
  // Set current user immediately
  if (window.currentAppUser?.uid) {
    organizersMap[window.currentAppUser.uid] = {
      name: window.currentAppUser?.profile?.name || 'You',
      profilePicture: window.currentAppUser?.profile?.profilePicture
    };
  }

  // Fetch all others in parallel
  if (otherCreatorIds.length > 0) {
    try {
      const { data } = await supabase
        .from('users')
        .select('id, name, profile_picture')
        .in('id', otherCreatorIds);
      if (data) {
        data.forEach(u => {
          organizersMap[u.id] = { name: u.name, profilePicture: u.profile_picture };
        });
      }
    } catch (err) {
      console.error('Error fetching organizers:', err);
    }
  }

// Separate events into categories
const ongoing = [];
const upcoming = [];
const finished = [];

eventsAtPlace.forEach(event => {
  // ✅ USE MASTER STATUS CALCULATOR
  const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
  const hasEnded = status.hasEnded;
  const hasStarted = status.hasStarted;
  
  if (hasEnded) {
    finished.push(event);
  } else if (hasStarted && status.endDate) {
    ongoing.push(event);
  } else {
    upcoming.push(event);
  }
});

// Sort ONGOING by end date (ending soonest first)
ongoing.sort((a, b) => {
  const statusA = window.calculateEventStatus(a.startDateTime, a.endDateTime);
  const statusB = window.calculateEventStatus(b.startDateTime, b.endDateTime);
  return statusA.endDate - statusB.endDate;
});

// Sort UPCOMING by start date (starting soonest first)
upcoming.sort((a, b) => {
  const statusA = window.calculateEventStatus(a.startDateTime, a.endDateTime);
  const statusB = window.calculateEventStatus(b.startDateTime, b.endDateTime);
  return statusA.startDate - statusB.startDate;
});

// Sort FINISHED by end date (most recently finished first)
finished.sort((a, b) => {
  const statusA = window.calculateEventStatus(a.startDateTime, a.endDateTime);
  const statusB = window.calculateEventStatus(b.startDateTime, b.endDateTime);
  return statusB.endDate - statusA.endDate;
});

// Combine arrays: ongoing first (sorted by end date), then upcoming, then finished
const sortedEvents = [...ongoing, ...upcoming, ...finished];

// If only one event qualifies, open details directly instead of list
if (sortedEvents.length === 1) {
  if (window.showEventDetailsPanel) {
    window.showEventDetailsPanel(sortedEvents[0], sortedEvents[0].placePhotoUrl || '');
  }
  return;
}

if (sortedEvents.length === 0) return;

// Use the photo from the first event in the SORTED list
const placePhotoUrl = sortedEvents[0]?.placePhotoUrl || '';

    // Remove existing panel if any
    const existingPanel = document.getElementById('eventsListPanel');
    if (existingPanel) {
      existingPanel.remove();
    }

    const panel = document.createElement('div');
    panel.id = 'eventsListPanel';
    panel.style.cssText = `
      position: fixed;
top: 79px;
right: -400px;
  width: 380px;
  max-width: calc(100vw - 4px);
height: calc(100vh - 79px - var(--footer-height, 50px));
border-bottom-left-radius: 20px;
border-top-left-radius: 20px;
      background: white;
      box-shadow: -4px 0 12px rgba(0,0,0,0.15);
      z-index: 1000;
      transition: right 0.3s ease-out;
      overflow-y: auto;
    `;

const eventsHtml = sortedEvents.map((event, index) => {
  const uniqueEventId = `${event.placeId}_${event.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');

  // ✅ USE MASTER STATUS CALCULATOR (handles timezone correctly)
  const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
  const hasStarted = status.hasStarted;
  const hasEnded = status.hasEnded;
  const isOngoing = status.isOngoing;
  const startDate = status.startDate;
  const endDate = status.endDate;
  const now = status.now;
  
  const startDisplay = formatDateTime(event.startDateTime);
  const endDisplay = formatDateTime(event.endDateTime);

// Calculate event status
const timeUntilStart = startDate - now;
const daysUntilStart = timeUntilStart / (1000 * 60 * 60 * 24);

// ✅ Only show countdown if event HASN'T STARTED yet AND is within 30 days
const showCountdown = !hasStarted && daysUntilStart > 0 && daysUntilStart <= 30;

// Determine if we should show pulse effect on the card border
// Show pulse for ALL non-finished events (ongoing and upcoming)
const shouldShowPulse = !hasEnded;

// Calculate progress using master calculator result
let progressColor = status.statusColor;
let progressText = status.statusText;


return `
<div 
    onclick="openEventDetailsFromList('${event.placeId}', '${event.startDateTime}')"
    style="
    padding: 16px;
    border: 2px solid ${isOngoing ? progressColor : (hasEnded ? '#ea4335' : (timeUntilStart < (24 * 60 * 60 * 1000) ? '#34a853' : '#959393'))};
    border-radius: 12px;
    margin-bottom: 12px;
    position: relative;
    overflow: visible;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    cursor: pointer;
  "
  onmouseenter="this.style.boxShadow='inset 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 6px rgba(0, 0, 0, 0.1)'; this.style.transform='scale(0.98)'"
  onmouseleave="this.style.boxShadow='0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)'; this.style.transform='scale(1)'"
  >
  <!-- Crown Badge (if organizer) OR Checkmark Badge (if participant) -->
${(() => {
  const isOrganizer = event.creatorId === window.currentAppUser?.uid;
  const isParticipant = event.participants?.includes(window.currentAppUser?.uid) && !isOrganizer;
  
  if (isOrganizer) {
    return `
      <div style="
        position: absolute;
        top: -10px;
        right: -10px;
        width: 25px;
        height: 25px;
        background: #e1e1e1fa;
        border: 2px solid rgba(0, 158, 177, 1);
        border-radius: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
        z-index: 10;
      ">
        <span style="font-size: 16px; line-height: 1; margin-top: -2px;">👑</span>
      </div>
    `;
  } else if (isParticipant) {
    return `
      <div style="
        position: absolute;
        top: -10px;
        right: -10px;
        width: 25px;
        height: 25px;
        background: white;
        border: 2px solid rgba(52, 168, 83, 1);
        border-radius: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
        z-index: 10;
      ">
        <span style="font-size: 16px; line-height: 1; color: green;">✓</span>
      </div>
    `;
  }
  return '';
})()}
      <!-- Pulsing border effect - ONLY if should show pulse -->
      ${shouldShowPulse ? `
        <style>
          @keyframes eventCardPulse_${index} {
            0% { 
              transform: scale(1);
              opacity: ${hasEnded ? '0' : '0.6'};
            }
            50% { 
              transform: scale(1.02);
              opacity: ${hasEnded ? '0' : '0.3'};
            }
            100% { 
              transform: scale(1.04);
              opacity: ${hasEnded ? '0' : '0'};
            }
          }
        </style>
        <div style="
          position: absolute;
          inset: -6px;
          border-radius: 14px;
          border: 2px solid ${isOngoing ? progressColor : (hasEnded ? '#ea4335' : (timeUntilStart < (24 * 60 * 60 * 1000) ? '#34a853' : '#959393'))};
          animation: ${hasEnded ? 'none' : `eventCardPulse_${index} 2s ease-in-out infinite`};
          pointer-events: none;
          z-index: -1;
          opacity: ${hasEnded ? '0' : '1'};
        "></div>
      ` : ''}
          
<!-- Event Title -->
<h3 style="
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #202124;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.3;
">
  ${event.title}
</h3>

<!-- Description -->
<div style="margin-bottom: 8px;">
  <p style="
    margin: 0;
    font-size: 13px;
    color: #5f6368;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
  ">
    ${(() => {
      const maxLength = 150;
      const trimmedDescription = event.description.replace(/^\s+|\s+$/g, '');
      return trimmedDescription.length > maxLength 
        ? trimmedDescription.substring(0, maxLength).trim() + '...'
        : trimmedDescription;
    })()}
  </p>
</div>

<!-- Start Time -->
<div style="
  font-size: 12px;
  color: #5f6368;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
">
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
  ${startDisplay}
</div>

<!-- End Time -->
<div style="
  font-size: 12px;
  color: #5f6368;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
">
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
    <polyline points="12 12 8 10"></polyline>
  </svg>
  ${endDisplay}
</div>

<!-- Organizer with Profile Picture -->
${(() => {
  const isCurrentUser = event.creatorId === window.currentAppUser?.uid;
  const organizer = organizersMap[event.creatorId] || { 
    name: event.createdBy, 
    profilePicture: null 
  };
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(organizer.name || 'User')}&background=e8eaed&color=5f6368&size=32`;
  
  return `
    <div style="
      font-size: 12px;
      color: #202124;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: ${showCountdown || isOngoing || hasEnded ? '12px' : '0'};
    ">
      <img 
        src="${organizer.profilePicture || fallbackUrl}"
        alt="${organizer.name}"
        style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid ${isCurrentUser ? 'rgba(0, 158, 177, 1)' : '#e8eaed'};
          flex-shrink: 0;
        "
        onerror="this.src='${fallbackUrl}'"
      />
      <span>${organizer.name}</span>
    </div>
  `;
})()}

          <!-- Countdown Timer (only if within 30 days and hasn't started) -->
${showCountdown ? `
  <div 
    id="countdown_${uniqueEventId}"
    data-event-time="${event.startDateTime}"
    style="
      text-align: center;
      padding: 12px 0;
    "
  >
    <div style="
      display: flex;
      justify-content: center;
      align-items: flex-end;
      gap: 8px;
    ">
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span style="
          font-size: 20px;
          font-weight: 600;
          color: #202124;
          letter-spacing: 1px;
          min-width: 30px;
          display: inline-block;
        " id="countdown_${uniqueEventId}_days">00</span>
        <span style="
          font-size: 10px;
          color: #5f6368;
          font-weight: 500;
          margin-top: 2px;
          text-transform: uppercase;
        ">days</span>
      </div>
      
      <span style="
        font-size: 20px;
        font-weight: 600;
        color: #202124;
        margin-bottom: 16px;
      ">:</span>
      
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span style="
          font-size: 20px;
          font-weight: 600;
          color: #202124;
          letter-spacing: 1px;
          min-width: 30px;
          display: inline-block;
        " id="countdown_${uniqueEventId}_hours">00</span>
        <span style="
          font-size: 10px;
          color: #5f6368;
          font-weight: 500;
          margin-top: 2px;
          text-transform: uppercase;
        ">hours</span>
      </div>
      
      <span style="
        font-size: 20px;
        font-weight: 600;
        color: #202124;
        margin-bottom: 16px;
      ">:</span>
      
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span style="
          font-size: 20px;
          font-weight: 600;
          color: #202124;
          letter-spacing: 1px;
          min-width: 30px;
          display: inline-block;
        " id="countdown_${uniqueEventId}_minutes">00</span>
        <span style="
          font-size: 10px;
          color: #5f6368;
          font-weight: 500;
          margin-top: 2px;
          text-transform: uppercase;
        ">minutes</span>
      </div>
      
      <span style="
        font-size: 20px;
        font-weight: 600;
        color: #202124;
        margin-bottom: 16px;
      ">:</span>
      
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span style="
          font-size: 20px;
          font-weight: 600;
          color: #202124;
          letter-spacing: 1px;
          min-width: 30px;
          display: inline-block;
        " id="countdown_${uniqueEventId}_seconds">00</span>
        <span style="
          font-size: 10px;
          color: #5f6368;
          font-weight: 500;
          margin-top: 2px;
          text-transform: uppercase;
        ">seconds</span>
      </div>
    </div>
  </div>
` : ''}

<!-- Status Circle (for ongoing events) -->
${isOngoing ? `
  <div 
    id="status_${uniqueEventId}"
    style="
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 8px;
    "
  >
<!-- Status circle with pulsing effect -->
<div style="position: relative; width: 14px; height: 14px; display: flex; align-items: center; justify-content: center;">
  <style>
    @keyframes smallPulse_${uniqueEventId} {
      0% { transform: scale(1); opacity: 0.6; }
      50% { transform: scale(1.4); opacity: 0.3; }
      100% { transform: scale(1.8); opacity: 0; }
    }
  </style>
  <div 
    id="pulse_${uniqueEventId}"
    style="
      position: absolute;
      width: 14px;
      height: 14px;
      border: 3px solid ${progressColor};
      border-radius: 50%;
      opacity: 0.4;
      animation: smallPulse_${uniqueEventId} 2s ease-in-out infinite;
    "
  ></div>
  <div 
    id="circle_${uniqueEventId}"
    style="
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: ${progressColor};
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      position: relative;
      z-index: 1;
    "
  ></div>
</div>

<!-- Status text -->
<span 
  id="text_${uniqueEventId}"
  style="
    font-size: 14px;
    font-weight: 400;
    color: ${progressColor};
  "
>${progressText}</span>
  </div>
` : ''}

<!-- Finished status circle -->
${hasEnded ? `
  <div 
    style="
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 8px;
    "
  >
<!-- Status circle WITHOUT pulsing effect for finished events -->
<div style="position: relative; width: 14px; height: 14px; display: flex; align-items: center; justify-content: center;">
  <div style="
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ea4335;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    position: relative;
    z-index: 1;
  "></div>
</div>
    
    <!-- Status text -->
    <span style="
      font-size: 14px;
      font-weight: 600;
      color: #ea4335;
    ">Finished</span>
  </div>
` : ''}
        </div>
      `;
    }).join('');

    panel.innerHTML = `
      <div style="padding: 20px;">
<!-- Close button -->
<button 
  onclick="window.closeAllPanels()"
  style="
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    font-size: 28px;
    color: #5f6368;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  "
>×</button>

        <!-- Header -->
        <h2 style="margin: 0 0 20px 0; font-size: 24px; font-weight: 600; color: #202124; padding-right: 40px;">
          All Events
        </h2>

<!-- Place Info Card (same style as single event) -->
<div style="
  display: flex;
  align-items: start;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #202124;
  position: relative;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15);
">
  <img 
    src="${placePhotoUrl}" 
    alt="${placeName}" 
    style="width: 48px; height: 48px; border-radius: 8px; object-fit: cover; flex-shrink: 0;"
  />
<div style="flex: 1; min-width: 0; padding-right: 45px;">
  <h3 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #202124; line-height: 1.3;">
    ${placeName}
  </h3>
  <p style="margin: 0; font-size: 12px; color: #5f6368; display: flex; align-items: start; gap: 4px;">
    <span style="flex-shrink: 0;">📍</span>
    <span>${placeId.startsWith('custom_') ? `${sortedEvents[0].latitude.toFixed(6)}, ${sortedEvents[0].longitude.toFixed(6)}` : placeAddress}</span>
  </p>
</div>
  
<!-- Google Maps Button -->
<button
  onclick="(window.openExternal||window.open)('${getGoogleMapsUrl(placeId, eventsAtPlace[0].latitude, eventsAtPlace[0].longitude)}', '_blank')"
  style="
    position: absolute;
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    background: #1967D2;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    transition: all 0.2s;
    padding: 4px;
  "
  onmouseover="this.style.background='#1557B0'; this.style.boxShadow='0 3px 8px rgba(0,0,0,0.25)'; this.style.transform='scale(1.05)'"
  onmouseout="this.style.background='#1967D2'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.15)'; this.style.transform='scale(1)'"
  title="View in Google Maps"
>
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
  </svg>
</button>
</div>

<!-- Events Count -->
<div style="
  background: #f1f3f4;
  border-left: 3px solid #202124;
  padding: 8px 12px;
  margin-bottom: 16px;
  border-radius: 4px;
">
  <span style="font-size: 13px; font-weight: 600; color: #202124;">
    ${eventsAtPlace.length} event${eventsAtPlace.length > 1 ? 's' : ''} at this location
  </span>
</div>

        <!-- Events List -->
        <div style="margin-top: 16px;">
          ${eventsHtml}
        </div>
      </div>
    `;

    document.body.appendChild(panel);

// Initialize intervals array if it doesn't exist
if (!window.eventIntervals) {
  window.eventIntervals = [];
}

// Start countdown timers and progress bars
sortedEvents.forEach((event, eventIndexInArray) => {
    // Create UNIQUE event ID - MUST match the one in HTML
    const uniqueEventId = `${event.placeId}_${event.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
    
    // ✅ USE MASTER STATUS CALCULATOR
    const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
    const hasStarted = status.hasStarted;
    const hasEnded = status.hasEnded;
    const isOngoing = status.isOngoing;
    const startDate = status.startDate;
    const endDate = status.endDate;
    const now = status.now; // ← ADD THIS LINE
  
  const timeUntilStart = startDate - now;
  const daysUntilStart = timeUntilStart / (1000 * 60 * 60 * 24);
  
  if (daysUntilStart > 0 && daysUntilStart <= 30) {
    // Countdown timer - create a closure with UNIQUE ID
    (function(eventId, eventStartTime) {
      // Initial update
      updateCountdown(eventId, eventStartTime);
      
      // Set up interval
      const interval = setInterval(() => {
        const stillActive = updateCountdown(eventId, eventStartTime);
        if (!stillActive) {
          clearInterval(interval);
        }
      }, 1000);
      
      window.eventIntervals.push(interval);
    })(uniqueEventId, event.startDateTime);
    
  } else if (isOngoing) {
    // Progress bar - create a closure with UNIQUE ID
    (function(eventId, eventStartTime, eventEndTime) {
      // Initial update
      updateProgressBar(eventId, eventStartTime, eventEndTime);
      
      // Set up interval
      const interval = setInterval(() => {
        const stillActive = updateProgressBar(eventId, eventStartTime, eventEndTime);
        if (!stillActive) {
          clearInterval(interval);
        }
      }, 1000);
      
      window.eventIntervals.push(interval);
    })(uniqueEventId, event.startDateTime, event.endDateTime);
  }
});


// Add a master status checker that runs every second
const statusCheckInterval = setInterval(() => {
  const panelStillExists = document.getElementById('eventsListPanel');
  if (!panelStillExists) {
    clearInterval(statusCheckInterval);
    return;
  }
  
  const now = new Date();
  const oneMinuteInMs = 60 * 1000;
  
sortedEvents.forEach((event, eventIndexInArray) => {
    const uniqueEventId = `${event.placeId}_${event.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
    
    // ✅ USE MASTER STATUS CALCULATOR
    const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
    const hasStarted = status.hasStarted;
    const hasEnded = status.hasEnded;
    const isOngoing = status.isOngoing;
    const startDate = status.startDate;
    const endDate = status.endDate;
    const countdownElement = document.getElementById(`countdown_${uniqueEventId}`);
    const statusElement = document.getElementById(`status_${uniqueEventId}`);
    
    // Check if event finished more than 1 minute ago
    if (hasEnded && endDate) {
      const timeSinceEnd = now - endDate;
      
      if (timeSinceEnd > oneMinuteInMs) {
        // Find and remove the event card from DOM
        const eventCard = document.querySelector(`[id*="${uniqueEventId}"]`)?.closest('div[style*="border:"]');
        if (eventCard) {
          // Smooth fade out animation
          eventCard.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
          eventCard.style.opacity = '0';
          eventCard.style.transform = 'translateX(20px)';
          
          setTimeout(() => {
            eventCard.remove();
            
            // Remove from sortedEvents array
            const indexToRemove = sortedEvents.findIndex(e => 
              `${e.placeId}_${e.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_') === uniqueEventId
            );
            if (indexToRemove > -1) {
              sortedEvents.splice(indexToRemove, 1);
            }
            
            // Update the event count text
            const countText = document.querySelector('div[style*="background: #f1f3f4"]');
            if (countText) {
              const remainingCount = sortedEvents.length;
              countText.innerHTML = `
                <span style="font-size: 13px; font-weight: 600; color: #202124;">
                  ${remainingCount} event${remainingCount !== 1 ? 's' : ''} at this location
                </span>
              `;
              
              // If no events left, show a message or close panel
              if (remainingCount === 0) {
                setTimeout(() => {
                  if (window.cleanupEventIntervals) {
                    window.cleanupEventIntervals();
                  }
                  const panel = document.getElementById('eventsListPanel');
                  if (panel) {
                    panel.style.right = '-400px';
                    setTimeout(() => panel.remove(), 300);
                  }
                }, 500);
              }
            }
          }, 300);
        }
        
// Remove from localStorage
for (let i = 0; i < localStorage.length; i++) {
  const key = localStorage.key(i);
  if (key.startsWith('event:')) {
    try {
      const storedEvent = JSON.parse(localStorage.getItem(key));
      const storedEventId = `${storedEvent.placeId}_${storedEvent.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
      if (storedEventId === uniqueEventId) {
        localStorage.removeItem(key);
        
        // Trigger map reload to update marker
        window.dispatchEvent(new CustomEvent('eventRemoved', { 
          detail: { placeId: event.placeId } 
        }));
        
        break;
      }
    } catch (e) {
      console.error('Error removing event:', e);
    }
  }
}
        
        return; // Skip the rest of the checks for this event
      }
    }
    
    // Event just started - replace countdown with status circle
    // ONLY if countdown exists AND status doesn't exist yet
    if (countdownElement && hasStarted && !hasEnded && endDate && !statusElement) {
      // Hide countdown
      countdownElement.style.display = 'none';
      
      // Create and insert status element
      const statusHTML = `
        <div 
          id="status_${uniqueEventId}"
          style="
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 8px;
          "
        >
          <div style="position: relative; width: 14px; height: 14px; display: flex; align-items: center; justify-content: center;">
            <style>
              @keyframes smallPulse_${uniqueEventId}_new {
                0% { transform: scale(1); opacity: 0.6; }
                50% { transform: scale(1.4); opacity: 0.3; }
                100% { transform: scale(1.8); opacity: 0; }
              }
            </style>
            <div 
              id="pulse_${uniqueEventId}"
              style="
                position: absolute;
                width: 14px;
                height: 14px;
                border: 3px solid #34a853;
                border-radius: 50%;
                opacity: 0.4;
                animation: smallPulse_${uniqueEventId}_new 2s ease-in-out infinite;
              "
            ></div>
            <div 
              id="circle_${uniqueEventId}"
              style="
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: #34a853;
                box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                position: relative;
                z-index: 1;
              "
            ></div>
          </div>
          <span 
            id="text_${uniqueEventId}"
            style="
              font-size: 14px;
              font-weight: 400;
              color: #34a853;
            "
          >Started</span>
        </div>
      `;
      
      // Insert after the countdown element
      countdownElement.insertAdjacentHTML('afterend', statusHTML);
      
      // Update event card border to green
      updateEventCardStatus(uniqueEventId, event);
      
      // Start progress bar interval for this event
      (function(eventId, eventStartTime, eventEndTime) {
        const interval = setInterval(() => {
          const stillActive = updateProgressBar(eventId, eventStartTime, eventEndTime);
          if (!stillActive) {
            clearInterval(interval);
          }
        }, 1000);
        
        window.eventIntervals.push(interval);
      })(uniqueEventId, event.startDateTime, event.endDateTime);
    }
    
    // Event just finished - update existing status to finished
    // ONLY if status exists and it's not already showing "Finished"
    if (statusElement && hasEnded) {
      const textElement = document.getElementById(`text_${uniqueEventId}`);
      
      // Check if it's not already marked as finished
      if (textElement && textElement.textContent !== 'Finished') {
        const circleElement = document.getElementById(`circle_${uniqueEventId}`);
        const pulseElement = document.getElementById(`pulse_${uniqueEventId}`);
        
        if (circleElement) {
          // Update to finished state
          circleElement.style.background = '#ea4335';
          textElement.textContent = 'Finished';
          textElement.style.color = '#ea4335';
          textElement.style.fontWeight = '600';
          
          // Remove pulse animation
          if (pulseElement) {
            pulseElement.style.opacity = '0';
            pulseElement.style.animation = 'none';
          }
          
          // Update event card border to red and remove pulse
          updateEventCardStatus(uniqueEventId, event);
          
          // Find the event card and smoothly move it to bottom
          const eventCard = statusElement.closest('div[style*="border:"]');
          if (eventCard && eventCard.parentElement) {
            // Add smooth transition
            eventCard.style.transition = 'opacity 0.3s ease-out';
            eventCard.style.opacity = '0.5';
            
            setTimeout(() => {
              // Move to bottom
              eventCard.parentElement.appendChild(eventCard);
              
              // Fade back in
              setTimeout(() => {
                eventCard.style.opacity = '1';
                setTimeout(() => {
                  eventCard.style.transition = '';
                }, 300);
              }, 50);
            }, 300);
          }
        }
      }
    }
  });
}, 1000);

// Add to intervals for cleanup
window.eventIntervals.push(statusCheckInterval);

// Add continuous visual status updates (borders, colors)
const visualUpdateInterval = setInterval(() => {
  const panelStillExists = document.getElementById('eventsListPanel');
  if (!panelStillExists) {
    clearInterval(visualUpdateInterval);
    return;
  }
  
  sortedEvents.forEach((event) => {
    const uniqueEventId = `${event.placeId}_${event.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
    
    // ✅ USE MASTER STATUS CALCULATOR
    const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
    const hasStarted = status.hasStarted;
    const hasEnded = status.hasEnded;
    const isOngoing = status.isOngoing;
    
    // Update card styling
    updateEventCardStatus(uniqueEventId, event);
    
    // If ongoing, update the progress status (color transitions)
    if (isOngoing) {
      updateProgressBar(uniqueEventId, event.startDateTime, event.endDateTime);
    }
  });
}, 1000);

// Add to intervals for cleanup
window.eventIntervals.push(visualUpdateInterval);

    // Slide in animation
    setTimeout(() => {
      panel.style.right = '0';
    }, 10);

    // Make it globally available for back navigation
window.showEventsListPanel = showEventsListPanel;
  };

  export const highlightNewEvent = (eventData) => {
  // Create unique event ID
  const uniqueEventId = `${eventData.placeId}_${eventData.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
  
  // Find the event card by looking for any element with this ID
  const eventElements = document.querySelectorAll(`[id*="${uniqueEventId}"]`);
  let eventCard = null;
  
  for (let elem of eventElements) {
    const card = elem.closest('div[style*="border:"]');
    if (card) {
      eventCard = card;
      break;
    }
  }
  
  if (!eventCard) {
    return;
  }
  
  // Scroll to the event card smoothly
  eventCard.scrollIntoView({ 
    behavior: 'smooth', 
    block: 'center'
  });
  
  // Store original styles
  const originalBoxShadow = eventCard.style.boxShadow || '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)';
  const originalTransform = eventCard.style.transform || 'scale(1)';
  
  // Wait a bit for scroll to complete
  setTimeout(() => {
    // First pulse
    eventCard.style.transition = 'all 0.6s ease-out';
    eventCard.style.transform = 'scale(1.05)';
    eventCard.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.25), 0 6px 16px rgba(0, 0, 0, 0.2)';
    
    setTimeout(() => {
      // Return to normal
      eventCard.style.transform = 'scale(1)';
      eventCard.style.boxShadow = originalBoxShadow;
      
      setTimeout(() => {
        // Second pulse
        eventCard.style.transform = 'scale(1.05)';
        eventCard.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.25), 0 6px 16px rgba(0, 0, 0, 0.2)';
        
        setTimeout(() => {
          // Final return to normal
          eventCard.style.transform = originalTransform;
          eventCard.style.boxShadow = originalBoxShadow;
          
          // Remove transition after animation completes
          setTimeout(() => {
            eventCard.style.transition = '';
          }, 600);
        }, 600);
      }, 600);
    }, 600);
  }, 800);
};

  // Function to update countdown timer
export const updateCountdown = (eventId, eventTime) => {
  try {
    const daysElement = document.getElementById(`countdown_${eventId}_days`);
    const hoursElement = document.getElementById(`countdown_${eventId}_hours`);
    const minutesElement = document.getElementById(`countdown_${eventId}_minutes`);
    const secondsElement = document.getElementById(`countdown_${eventId}_seconds`);
    
    if (!daysElement || !hoursElement || !minutesElement || !secondsElement) {
      return false;
    }

    const now = new Date();
    
    // Parse as UTC but treat as local time
    // Remove the timezone suffix and parse as local
    const eventTimeLocal = eventTime.replace(/[+-]\d{2}:\d{2}$/, '').replace('Z', '');
    const eventDate = new Date(eventTimeLocal);
    
    const diff = eventDate.getTime() - now.getTime();

    if (diff <= 0) {
      daysElement.textContent = '00';
      hoursElement.textContent = '00';
      minutesElement.textContent = '00';
      secondsElement.textContent = '00';
      return false;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const newDays = String(days).padStart(2, '0');
    const newHours = String(hours).padStart(2, '0');
    const newMinutes = String(minutes).padStart(2, '0');
    const newSeconds = String(seconds).padStart(2, '0');

    if (daysElement.textContent !== newDays) daysElement.textContent = newDays;
    if (hoursElement.textContent !== newHours) hoursElement.textContent = newHours;
    if (minutesElement.textContent !== newMinutes) minutesElement.textContent = newMinutes;
    if (secondsElement.textContent !== newSeconds) secondsElement.textContent = newSeconds;
    
    return true;
  } catch (error) {
    return false;
  }
};

// Global function to clean up all event intervals
export const cleanupEventIntervals = function() {
  if (window.eventIntervals) {
    window.eventIntervals.forEach(interval => {
      if (interval) clearInterval(interval);
    });
    window.eventIntervals = [];
  }
};

window.showEventsListPanel = showEventsListPanel;
window.highlightNewEvent = highlightNewEvent;
window.cleanupEventIntervals = cleanupEventIntervals;
window.updateCountdown = updateCountdown;